import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, Chip, Divider, CircularProgress,
    Grid, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import useAuth from '../../hooks/useAuth';
import { publicAPI } from '../../services/api';

export default function OfferDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadOffer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadOffer = async () => {
        try {
            const { data } = await publicAPI.getJobOffer(id);
            setOffer(data);
        } catch (err) {
            setError('Offre non trouvée');
        } finally {
            setLoading(false);
        }
    };

    const handlePostuler = () => {
        if (!user) {
            navigate(`/login?redirect=/offres/${id}/postuler`);
        } else if (user.role === 'trainer') {
            navigate(`/trainer/postuler/${id}`);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !offer) {
        return (
            <Box sx={{ textAlign: 'center', py: 10 }}>
                <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto' }}>{error || 'Offre non trouvée'}</Alert>
                <Button component={Link} to="/" sx={{ mt: 2 }}>Retour aux offres</Button>
            </Box>
        );
    }

    return (
        <Box>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                sx={{ mb: 4, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
            >
                Retour aux offres
            </Button>

            <Paper elevation={0} sx={{ 
                borderRadius: 6, 
                overflow: 'hidden', 
                bgcolor: 'white',
                border: '1px solid rgba(0,0,0,0.05)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
            }}>
                {/* Header */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #1b263b 0%, #415a77 100%)',
                    p: { xs: 4, md: 6 }, 
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                        {offer.ista && (
                            <Chip
                                label={offer.ista.nom}
                                sx={{ 
                                    bgcolor: 'rgba(255,255,255,0.15)', 
                                    color: 'white', 
                                    fontWeight: 700,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.1)'
                                }}
                            />
                        )}
                        {offer.types?.map(t => (
                            <Chip
                                key={t}
                                label={t}
                                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600 }}
                            />
                        ))}
                    </Box>
                    <Typography variant="h3" sx={{ 
                        fontWeight: 800, 
                        letterSpacing: '-0.02em',
                        mb: 2,
                        textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                        fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }
                    }}>
                        {offer.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, opacity: 0.8, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={500} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>{offer.ista?.ville || 'ISTA Local'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={500} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Publié récemment</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 4, md: 6 } }}>
                    {/* Key Info Cards */}
                    <Grid container spacing={2} sx={{ mb: 6 }}>
                        <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                borderRadius: 4, 
                                bgcolor: '#f8fafc', 
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 2, sm: 3 }
                            }}>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(27, 38, 59, 0.05)', borderRadius: 3 }}>
                                    <SchoolIcon sx={{ color: '#1b263b', fontSize: { xs: 24, sm: 32 } }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                                        Niveau(x) requis
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b263b', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {offer.required_levels?.join(', ')}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                borderRadius: 4, 
                                bgcolor: '#f8fafc', 
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 2, sm: 3 }
                            }}>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(27, 38, 59, 0.05)', borderRadius: 3 }}>
                                    <AccessTimeIcon sx={{ color: '#1b263b', fontSize: { xs: 24, sm: 32 } }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                                        Volume horaire
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b263b', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {offer.total_hours} Heures
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={6}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b263b', mb: 3 }}>
                                Description du poste
                            </Typography>
                            <Typography variant="body1" sx={{ 
                                color: '#475569', 
                                lineHeight: 1.8, 
                                whiteSpace: 'pre-line',
                                mb: 6 
                            }}>
                                {offer.description || 'Spécialiste recherché pour animer des modules de formation technique et accompagner les stagiaires dans leur montée en compétences professionnelle.'}
                            </Typography>

                            {offer.modules && offer.modules.length > 0 && (
                                <>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b263b', mb: 3 }}>
                                        Modules à enseigner
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 6 }}>
                                        {offer.modules.map(m => (
                                            <Chip
                                                key={m.id}
                                                label={`${m.name} (${m.masse_horaire}h)`}
                                                variant="outlined"
                                                sx={{ 
                                                    px: 1,
                                                    py: 2.5,
                                                    borderRadius: 2,
                                                    borderColor: '#e2e8f0',
                                                    color: '#1b263b',
                                                    fontWeight: 600,
                                                    bgcolor: '#ffffff',
                                                    '&:hover': { bgcolor: '#f1f5f9' }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper sx={{ 
                                p: { xs: 3, sm: 4 }, 
                                borderRadius: 5, 
                                bgcolor: '#f8fafc', 
                                border: '1px solid #e2e8f0',
                                position: { xs: 'relative', md: 'sticky' },
                                top: { md: 100 }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1b263b', mb: 2 }}>
                                    Postuler à cette offre
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mb: 4, lineHeight: 1.6 }}>
                                    {user ? 'Votre profil correspond-il à ces attentes ? Envoyez votre candidature dès maintenant.' : 'Veuillez vous connecter en tant que formateur pour pouvoir soumettre votre candidature.'}
                                </Typography>

                                {user && user.role !== 'trainer' ? (
                                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                                        Seuls les formateurs vacataires peuvent postuler.
                                    </Alert>
                                ) : (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handlePostuler}
                                        startIcon={<WorkIcon />}
                                        sx={{
                                            py: 2,
                                            fontWeight: 700,
                                            boxShadow: '0 10px 20px rgba(27, 38, 59, 0.2)',
                                        }}
                                    >
                                        Postuler maintenant
                                    </Button>
                                )}
                                
                                {!user && (
                                    <Button
                                        fullWidth
                                        component={Link}
                                        to="/register"
                                        sx={{ mt: 2, fontWeight: 700, textTransform: 'none' }}
                                    >
                                        Pas encore de compte ?
                                    </Button>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>

    );
}
